import * as React from "react"
import {
  type Category,
  Interval,
  type IntervalOrigin,
  useListActivePartyRolesQuery,
  useReadCategoryQuery,
} from "@digits-graphql/frontend/graphql-bearer"
import { LoggedRedirect } from "@digits-shared/components/Router/LoggedRedirect"
import dateTimeHelper from "@digits-shared/helpers/dateTimeHelper"
import useRouter from "@digits-shared/hooks/useRouter"
import dayjs from "@digits-shared/initializers/dayjs/dayjs"
import { type PartiesCollectionRoutesNames } from "src/frontend/components/OS/Shared/Parties/PartiesCollectionDisplayToggle"
import { useViewVersion } from "src/frontend/components/Shared/Contexts/useViewVersion"
import routes from "src/frontend/routes"
import { FrontendPartyRole, SUPPORTED_PARTY_ROLES } from "src/frontend/types/FrontendPartyRole"
import { TimeContext } from "src/shared/components/Contexts/TimeContext"
import { showBalancesForCategory } from "src/shared/helpers/categoryHelper"
import useIntervalOrigin, {
  useIntervalOriginWithDefaultCount,
} from "src/shared/hooks/useIntervalOrigin"

const OTHER_SIDE_ROUTE_NAMES = [
  routes.categoryDetailsPartyRoleOtherSide.name,
  routes.categoryDetailsPartyRoleWaterfallOtherSide.name,
]

export const CATEGORY_DETAILS_PARTY_BREAKDOWN_ROUTES: PartiesCollectionRoutesNames = {
  otherSideNames: OTHER_SIDE_ROUTE_NAMES,
  list: routes.categoryDetailsPartyRole.name,
  listOtherSide: routes.categoryDetailsPartyRoleOtherSide.name,
  waterfall: routes.categoryDetailsPartyRoleWaterfall.name,
  waterfallOtherSide: routes.categoryDetailsPartyRoleWaterfallOtherSide.name,
}

/*
  CONTEXT
*/

export interface FrontendPartyRoleDescriptor {
  role: FrontendPartyRole
  isOtherSide: boolean
}

export interface CategoryDetailsContextValue {
  frontendPartyRole: FrontendPartyRoleDescriptor
  availableFrontendPartyRoles: FrontendPartyRoleDescriptor[]
  categoryId: string
  category: Category | undefined
  loading: boolean
  isUglyOther: boolean
}

export const CategoryDetailsContext = React.createContext<CategoryDetailsContextValue>({
  frontendPartyRole: { role: FrontendPartyRole.Unknown, isOtherSide: false },
  availableFrontendPartyRoles: [],
  categoryId: "",
  category: undefined,
  loading: true,
  isUglyOther: false,
})

export const CategoryDetailsContextProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const viewKey = useViewVersion()

  const {
    location,
    params: { categoryId = "" },
  } = useRouter()

  const { data, loading } = useReadCategoryQuery({
    variables: { viewKey, categoryId },
    context: { avsBatch: true },
    skip: !categoryId,
  })

  const category = data?.readCategory
  const isUglyOther = location.queryParams.other === "true"

  const intervalOrigin = useIntervalOriginWithDefaultCount()

  const { frontendPartyRole, availableFrontendPartyRoles } = useAvailableFrontendPartyRoles(
    category,
    intervalOrigin,
    isUglyOther
  )

  const value = React.useMemo(
    () => ({
      availableFrontendPartyRoles,
      frontendPartyRole,
      categoryId,
      category,
      loading,
      isUglyOther,
    }),
    [availableFrontendPartyRoles, frontendPartyRole, categoryId, category, loading, isUglyOther]
  )

  if (!value.category && !loading) {
    return (
      <LoggedRedirect
        name="CategoryDetailsContextProvider-noCategory"
        to={routes.root.generate()}
      />
    )
  }

  return (
    <CategoryDetailsContext.Provider value={value}>
      <TimeContext.Provider value={intervalOrigin}>{children}</TimeContext.Provider>
    </CategoryDetailsContext.Provider>
  )
}

/*
  HOOKS
*/

export function useCategoryDetailsContext() {
  return React.useContext(CategoryDetailsContext)
}

export function useShowCategoryDetailsBalanceSummary() {
  const { category } = useCategoryDetailsContext()
  const intervalOrigin = useIntervalOrigin()
  const sixMonthsAgo = dayjs().subtract(6, "months")
  const startOfIntervalOrigin = dateTimeHelper.startOfIntervalOrigin(intervalOrigin)

  return React.useMemo(
    () =>
      !!category &&
      showBalancesForCategory(category) &&
      !(startOfIntervalOrigin.isBefore(sixMonthsAgo) && intervalOrigin.interval === Interval.Day),
    [category, intervalOrigin.interval, sixMonthsAgo, startOfIntervalOrigin]
  )
}

/**
 * Helper hook which determines the party roles that apply to the current category for the current interval origin,
 * and returns the current frontend party role determined from the URL.
 */
function useAvailableFrontendPartyRoles(
  category: Category | undefined,
  intervalOrigin: IntervalOrigin,
  isUglyOther: boolean
): Pick<CategoryDetailsContextValue, "frontendPartyRole" | "availableFrontendPartyRoles"> {
  const viewKey = useViewVersion()
  const {
    location: { name: locationName },
    params: { categoryId, partyRole },
  } = useRouter()

  const { data: avsRolesData } = useListActivePartyRolesQuery({
    variables: {
      intervalOrigin,
      filter: {
        viewKey,
        categoryId,
        withoutCategoryAncestors: isUglyOther,
      },
    },
    skip: !category,
    context: { avsBatch: true },
  })

  return React.useMemo(() => {
    const frontendPartyRole = {
      role: FrontendPartyRole.findByURLKey(partyRole),
      isOtherSide: OTHER_SIDE_ROUTE_NAMES.includes(locationName),
    }

    const availableFrontendPartyRoles = Array.from(avsRolesData?.listActivePartyRoles ?? [])
      .filter((role) => SUPPORTED_PARTY_ROLES.includes(role))
      .map((role) => ({ role: FrontendPartyRole.findByRole(role), isOtherSide: false }))
    return {
      frontendPartyRole,
      availableFrontendPartyRoles,
    }
  }, [avsRolesData?.listActivePartyRoles, locationName, partyRole])
}
